import { createElement, Fragment } from "react";
import { ElementType } from "domelementtype";
import parse, {
  Element,
  type DOMNode,
  type domToReact,
} from "html-react-parser";
import convertStyleToObject from "style-to-object";
import { AddBillButton } from "@components/AddBillButton/AddBillButton";
import { Link } from "@components/Link/Link";
import { OrderedList } from "@components/OrderedList/OrderedList";
import { Table } from "@components/Table/Table";

const camelize = (s: string): string =>
  s.replace(/-./g, (x) => (x[1] ?? "").toUpperCase());

const camelizeObjectKeys = (obj: Record<string, string> | null) =>
  Object.entries(obj ?? {}).reduce((op, [key, value]) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    op[camelize(key)] = value;
    return op;
  }, {});

const parseStyle = (style: string) =>
  camelizeObjectKeys(convertStyleToObject(style));

const parseLink = (node: Element) => {
  return createElement(
    Link,
    {
      to: node?.attribs?.href ?? "",
      rel: "nofollow",
      ...(node?.attribs?.target
        ? {
            target: node?.attribs?.target,
          }
        : {}),
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    parseNodeArray(node?.children),
  );
};

const parseAddBillButton = (node: Element) => {
  return createElement(
    AddBillButton,
    {
      jointBillServiceId: parseInt(node?.attribs.jointbillserviceid ?? "0"),
      serviceProviderId: parseInt(node?.attribs.serviceproviderid ?? "0"),
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    parseNodeArray(node?.children),
  );
};

const parseTable = (node: Element) => {
  delete node.attribs.class;
  delete node.attribs.style;

  return createElement(
    Table,
    node.attribs,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    parseNodeArray(node?.children),
  );
};

const parseOrderedList = (node: Element) => {
  delete node.attribs.class;
  delete node.attribs.style;

  const styleAllowList = ["listStyleType"];

  const parsedStyles: Record<string, string> = parseStyle(
    node?.attribs?.style ?? "",
  );

  const filteredStyles = Object.keys(parsedStyles)
    .filter((key) => styleAllowList.includes(key))
    .reduce(
      (result, key) => {
        result[key] = parsedStyles[key] ?? "";
        return result;
      },
      {} as Record<string, string>,
    );

  return createElement(
    OrderedList,
    { ...node.attribs, style: { ...filteredStyles } },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    parseNodeArray(node?.children),
  );
};

const parseCustomElement = (node: Element) => {
  delete node.attribs.class;
  delete node.attribs.style;

  return createElement(
    node.name,
    { ...node.attribs, style: { ...parseStyle(node?.attribs?.style ?? "") } },
    node?.children?.length > 0
      ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        parseNodeArray(node?.children)
      : null,
  );
};

export const parseTag = (node: DOMNode) => {
  if (!(node instanceof Element)) {
    return null;
  }

  switch (node.name) {
    case "a":
      return parseLink(node);
    case "iframe":
    case "script":
    case "link":
      return createElement(Fragment);
    case "table":
      return parseTable(node);
    case "ol":
      return parseOrderedList(node);
    case "addbillbutton":
      return parseAddBillButton(node);
    default:
      return parseCustomElement(node);
  }
};

const parseText = (node: DOMNode): string => {
  return !(node instanceof Element) ? node.data : "";
};

const parseNode = (node: DOMNode): JSX.Element | string | null => {
  switch (node.type) {
    case ElementType.Tag:
      return parseTag(node);
    case ElementType.Text:
      return parseText(node);
    default:
      return createElement(Fragment);
  }
};

const parseNodeArray = (nodeArray: DOMNode[]) => {
  return nodeArray.map((node, key) =>
    createElement(Fragment, { key }, parseNode(node)),
  );
};

export const parseHtml = (html: string): ReturnType<typeof domToReact> => {
  if (typeof html !== typeof "") {
    return <></>;
  }

  return parse(html, {
    replace: (domNode) => {
      return parseNode(domNode);
    },
  });
};
